import { VARIANT_STAGE_TYPES } from '../../constants';

export const CLASSIFICATION_STAGE_ORDER = [
  VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  VARIANT_STAGE_TYPES.FREQUENCY_DATA,
  VARIANT_STAGE_TYPES.MOLECULAR_IMPACT,
];

export type StructuredArgument = {
  stage: string;
  section: string;
  text: string;
  comment?: string;
  selected: boolean;
  acmg_class: string;
  acmg_grade: string;
  acmg_rule_code: string;
  isFirstInStage?: boolean;
  isFirstInSection?: boolean;
  isStageRow?: boolean;
  isLastInSection?: boolean;
  id?: number;
  selection_type?: 'auto' | 'manual';
  selection_type_creation_date?: string;
  is_argument_met?: boolean;
  evidencedata?: Record<string, unknown> | null;
};

export const CLASSIFICATION_STAGE_DISPLAY_NAMES: Record<string, string> = {
  [VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION]: 'Clinical observation',
  [VARIANT_STAGE_TYPES.FREQUENCY_DATA]: 'Frequency data',
  [VARIANT_STAGE_TYPES.MOLECULAR_IMPACT]: 'Molecular impact',
};

export const structureArgumentsData = (
  data: any[],
  showMore: boolean,
): StructuredArgument[] => {
  // Group data by stage and section
  const groupedData = data.reduce(
    (acc: Record<string, Record<string, any[]>>, item) => {
      let stage = item.stage;
      if (!stage && item.section?.display) {
        if (item.section.display.toLowerCase().includes('clinical')) {
          stage = VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION;
        } else if (item.section.display.toLowerCase().includes('frequency')) {
          stage = VARIANT_STAGE_TYPES.FREQUENCY_DATA;
        } else if (item.section.display.toLowerCase().includes('molecular')) {
          stage = VARIANT_STAGE_TYPES.MOLECULAR_IMPACT;
        }
      }
      stage = stage || 'unknown';

      const sectionId = item.section?.id?.toString() || 'unknown';

      if (!acc[stage]) {
        acc[stage] = {};
      }
      if (!acc[stage][sectionId]) {
        acc[stage][sectionId] = [];
      }

      acc[stage][sectionId].push(item);
      return acc;
    },
    {},
  );

  const structuredData: StructuredArgument[] = [];

  CLASSIFICATION_STAGE_ORDER.forEach((stage) => {
    if (groupedData[stage]) {
      // Add stage row
      structuredData.push({
        stage: CLASSIFICATION_STAGE_DISPLAY_NAMES[stage] || stage,
        section: '',
        text: '',
        selected: false,
        acmg_class: '',
        acmg_grade: '',
        acmg_rule_code: '',
        isStageRow: true,
      });

      let isFirstSectionInStage = true;
      const processedSections = new Set<string>();

      Object.entries(groupedData[stage]).forEach(([sectionId, items]) => {
        const relevantItems = showMore
          ? items
          : items.filter((item) => item.selected === true);

        if (relevantItems.length > 0) {
          const sectionName = relevantItems[0].section?.display || '';
          const isNewSection = !processedSections.has(sectionName);
          processedSections.add(sectionName);

          relevantItems.forEach((item, index, array) => {
            structuredData.push({
              stage: CLASSIFICATION_STAGE_DISPLAY_NAMES[stage] || stage,
              section: item.section?.display || '',
              text: item.text || '',
              comment: item.comment,
              selected: item.is_argument_met === false ? false : item.selected,
              acmg_class: item.acmg_class || '',
              acmg_grade: item.acmg_grade || '',
              acmg_rule_code: item.acmg_rule_code || '',
              isFirstInStage: isFirstSectionInStage && index === 0,
              isFirstInSection: isNewSection && index === 0,
              isLastInSection: index === array.length - 1,
              id: item.id,
              selection_type: item.selection_type,
              selection_type_creation_date: item.selection_type_creation_date,
              is_argument_met: item.is_argument_met,
              evidencedata: item.evidencedata,
            });
          });

          if (relevantItems.length > 0) {
            isFirstSectionInStage = false;
          }
        }
      });
    }
  });

  // Also process any items in the 'unknown' stage if they exist
  if (groupedData['unknown']) {
    // Add unknown stage row
    structuredData.push({
      stage: 'Unknown',
      section: '',
      text: '',
      selected: false,
      acmg_class: '',
      acmg_grade: '',
      acmg_rule_code: '',
      isStageRow: true,
    });

    const processedSections = new Set<string>();

    Object.entries(groupedData['unknown']).forEach(([sectionId, items]) => {
      const relevantItems = showMore
        ? items
        : items.filter((item) => item.selected === true);

      if (relevantItems.length > 0) {
        const sectionName = relevantItems[0].section?.display || '';
        const isNewSection = !processedSections.has(sectionName);
        processedSections.add(sectionName);

        relevantItems.forEach((item, index) => {
          structuredData.push({
            stage: item.stage || 'Unknown',
            section: item.section?.display || '',
            text: item.text || '',
            comment: item.comment,
            evidencedata: item.evidencedata,
            selected: item.is_argument_met === false ? false : item.selected,
            acmg_class: item.acmg_class || '',
            acmg_grade: item.acmg_grade || '',
            acmg_rule_code: item.acmg_rule_code || '',
            isFirstInSection: isNewSection && index === 0,
            id: item.id,
            selection_type: item.selection_type,
            selection_type_creation_date: item.selection_type_creation_date,
            is_argument_met: item.is_argument_met,
          });
        });
      }
    });
  }

  return structuredData;
};
